import { useState, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  loadAverageBalanceStart,
  updateCategory,
} from '../../redux/categorySlice';
import { Button, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { dateToString } from '../../shared/formatters';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';

var today = new Date();
const PopupOverlay = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
}));

const PopupContainer = styled('div')(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
  width: '90%',
  maxWidth: '900px',
  maxHeight: '90vh',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    width: '95%',
    maxHeight: '95vh',
    borderRadius: '12px',
  },
}));

const PopupHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  borderBottom: '1px solid #eee',
  backgroundColor: 'rgba(0, 98, 105, 0.05)',
  borderTopLeftRadius: '16px',
  borderTopRightRadius: '16px',
  '& h2': {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#1a1a1a',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',
    '& h2': {
      fontSize: '1.25rem',
    },
  },
}));

const CloseBtn = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  fontSize: '1.5rem',
  cursor: 'pointer',
  color: '#666',
  '&:hover': {
    color: '#333',
  },
}));

const PopupContent = styled('div')(({ theme }) => ({
  padding: '24px',
  flexGrow: 1,
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const InputGroup = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '20px',
  gap: '10px',
  '& label': {
    fontWeight: 600,
    color: '#555',
    whiteSpace: 'nowrap',
    fontSize: '0.938rem',
  },
  '& input[type="number"]': {
    padding: '10px 14px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    width: '80px',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: 'rgb(0, 98, 105)',
    },
  },
  '& button': {
    backgroundColor: 'rgb(0, 98, 105)',
    color: 'white',
    padding: '10px 16px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgb(0, 120, 128)',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 12px rgba(0, 98, 105, 0.3)',
    },
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
    '& label': {
      whiteSpace: 'normal',
    },
    '& input[type="number"]': {
      width: '100%',
    },
    '& button': {
      width: '100%',
      padding: '12px 16px',
    },
  },
}));

const GoalsTable = styled('div')(({ theme }) => ({
  marginTop: '20px',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.9rem',
  },
  '& th, & td': {
    padding: '14px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #e0e0e0',
  },
  '& th': {
    backgroundColor: 'rgba(0, 98, 105, 0.05)',
    fontWeight: 700,
    color: '#1a1a1a',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  '& tr:last-child td': {
    borderBottom: 'none',
  },
  '& input[type="number"]': {
    width: '100px',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    transition: 'border-color 0.2s ease',
    '&:focus': {
      outline: 'none',
      borderColor: 'rgb(0, 98, 105)',
    },
  },
  [theme.breakpoints.down('md')]: {
    overflowX: 'auto',
    '& table': {
      minWidth: '600px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    border: 'none',
    boxShadow: 'none',
    '& table': {
      display: 'block',
      minWidth: 'auto',
    },
    '& thead': {
      display: 'none',
    },
    '& tbody': {
      display: 'block',
    },
    '& tr': {
      display: 'block',
      marginBottom: '16px',
      border: '1px solid #e0e0e0',
      borderRadius: '12px',
      backgroundColor: '#fff',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
    },
    '& td': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      borderBottom: '1px solid #f0f0f0',
      '&:last-child': {
        borderBottom: 'none',
        justifyContent: 'center',
        padding: '16px',
      },
      '&:not(:last-child)::before': {
        content: 'attr(data-label)',
        fontWeight: 700,
        color: '#666',
        fontSize: '0.813rem',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
      },
    },
    '& input[type="number"]': {
      width: '120px',
    },
  },
}));

const ActionBtns = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: theme.spacing(2),
  padding: '20px 0',
  borderTop: '1px solid #eee',
  marginTop: '20px',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse',
    gap: theme.spacing(1.5),
    padding: '16px 0',
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.5, 3),
  fontWeight: 600,
  textTransform: 'none',
  minHeight: '48px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.grey[200],
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
  },
  '&.MuiButton-sizeSmall': {
    minHeight: 'auto',
    padding: theme.spacing(1, 2),
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      padding: theme.spacing(1.5, 2),
    },
  },
}));

const TransferBtn = styled('button')(({ theme }) => ({
  background: 'none',
  border: 'none',
  fontSize: '1.2rem',
  cursor: 'pointer',
  color: 'rgb(0, 98, 105)',
  marginLeft: '10px',
  marginRight: '10px',
  padding: '4px 8px',
  borderRadius: '4px',
  transition: 'all 0.2s ease',
  '&:hover': {
    color: 'rgb(0, 120, 128)',
    backgroundColor: 'rgba(0, 98, 105, 0.1)',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.5rem',
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 98, 105, 0.1)',
    borderRadius: '8px',
  },
}));

function extractCategories(data, name) {
  if (!data || !data.groups) {
    return [];
  }

  const allCategories = [];

  data.groups.debit.forEach((group) => {
    if (group.categories && Array.isArray(group.categories)) {
      group.categories.forEach((item) => {
        if (
          name &&
          name !== '' &&
          item.category.name.toLowerCase().indexOf(name.toLowerCase()) === -1
        ) {
          return;
        }
        const categoryWithAverage = {
          ...item.category,
          average: item.balance.average,
        };
        allCategories.push(categoryWithAverage);
      });
    }
  });

  return allCategories;
}

const ConfigureGoalsPopup = ({ onClose, searchFilters }) => {
  const dispatch = useDispatch();
  const result = useSelector((state) => state.category.result);
  const loadAverageBalanceStartAction = useCallback(
    (filters) => dispatch(loadAverageBalanceStart(filters)),
    [dispatch]
  );
  const updateCategoryAction = useCallback(
    (category) => dispatch(updateCategory(category)),
    [dispatch]
  );

  const defaultNumberOfMonths = 6;
  const [months, setmonths] = useState(defaultNumberOfMonths);
  const [calculatedGoals, setCalculatedGoals] = useState([]);

  const loadCategoryAverages = useCallback(() => {
    const categories = extractCategories(result, searchFilters.name);

    setCalculatedGoals(categories);
  }, [searchFilters, result, setCalculatedGoals]);

  useEffect(() => {
    if (months > 0) {
      const newPayload = {
        from: dateToString(
          new Date(today.getFullYear(), today.getMonth() - months, 1)
        ),
      };
      const newSearchFilters = { ...searchFilters, ...newPayload };

      loadAverageBalanceStartAction(newSearchFilters);
    }
  }, [months, searchFilters, loadAverageBalanceStartAction]);

  useEffect(() => {
    loadCategoryAverages();
  }, [loadCategoryAverages]);

  const handleValueTransfer = (index, averageValue) => {
    const newsGoals = [...calculatedGoals];
    newsGoals[index].goal = Number(averageValue).toFixed(2);
    setCalculatedGoals(newsGoals);
  };

  const handleTransferAll = () => {
    const newsGoals = [...calculatedGoals];
    const newsGoalsAverage = newsGoals.map((goal) => ({
      ...goal,
      goal: goal.average.toFixed(2),
    }));
    setCalculatedGoals(newsGoalsAverage);
  };

  const handleSaveAll = () => {
    calculatedGoals.forEach((goal) => {
      updateCategoryAction(goal);
    });
    onClose();
  };

  const handleSaveIndividual = (categoryId) => {
    const newGoal = calculatedGoals.find((m) => m.id === categoryId);
    if (newGoal) {
      updateCategoryAction(newGoal);
    }
  };

  const handleEdit = (index, novoValor) => {
    const newGoals = [...calculatedGoals];
    newGoals[index].goal = novoValor;
    setCalculatedGoals(newGoals);
  };

  return (
    <PopupOverlay>
      <PopupContainer>
        <PopupHeader>
          <h2>Configurar Metas Mensais</h2>
          <CloseBtn onClick={onClose}>&times;</CloseBtn>
        </PopupHeader>
        <PopupContent>
          <InputGroup>
            <label htmlFor="months">Calcular média dos últimos (meses):</label>
            <input
              id="months"
              type="number"
              value={months}
              onChange={(e) => setmonths(e.target.value)}
              min="1"
            />
          </InputGroup>

          {calculatedGoals.length > 0 && (
            <>
              <GoalsTable>
                <table>
                  <thead>
                    <tr>
                      <th>Categoria</th>
                      <th>Média Calculada</th>
                      <th>
                        <TransferBtn onClick={() => handleTransferAll()}>
                          <span role="img" aria-label="transferir">
                            &#x2192;
                          </span>
                        </TransferBtn>
                      </th>
                      <th>Valor da Meta</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {calculatedGoals.map((goal, index) => (
                      <tr key={goal.id}>
                        <td data-label="Categoria">{goal.name}</td>
                        <td data-label="Média Calculada">R$ {goal.average.toFixed(2)}</td>
                        <td data-label="Transferir">
                          <TransferBtn
                            onClick={() =>
                              handleValueTransfer(index, goal.average)
                            }
                          >
                            <span role="img" aria-label="transferir">
                              &#x2192;
                            </span>
                          </TransferBtn>
                        </td>
                        <td data-label="Valor da Meta">
                          <TextField
                            type="number"
                            value={goal.goal}
                            variant="outlined"
                            size="small"
                            inputProps={{ step: '0.01' }}
                            onChange={(e) => handleEdit(index, e.target.value)}
                          />
                        </td>
                        <td data-label="Salvar">
                          <SecondaryButton
                            type="button"
                            variant="outlined"
                            onClick={() => handleSaveIndividual(goal.id)}
                            size="small"
                          >
                            <CheckIcon />
                          </SecondaryButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </GoalsTable>
              <ActionBtns>
                <SecondaryButton
                  type="button"
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={onClose}
                >
                  Cancelar
                </SecondaryButton>
                <PrimaryButton
                  type="button"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveAll}
                >
                  Aceitar Todos
                </PrimaryButton>
              </ActionBtns>
            </>
          )}
        </PopupContent>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default ConfigureGoalsPopup;
